import "./styles.css";
import type { Problem, ProblemInput, Solution, Tag } from "./types";

type View = "library" | "drills" | "tags";

const state: {
  view: View;
  problems: Problem[];
  tags: Tag[];
  filters: { search: string; difficulty: string; status: string; tag: string };
} = {
  view: "library",
  problems: [],
  tags: [],
  filters: { search: "", difficulty: "", status: "", tag: "" },
};

const app = document.querySelector<HTMLDivElement>("#app")!;
const toast = document.querySelector<HTMLDivElement>("#toast")!;

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function showToast(message: string, kind: "success" | "error" = "success"): void {
  toast.textContent = message;
  toast.className = `toast visible ${kind}`;
  window.setTimeout(() => (toast.className = "toast"), 2600);
}

async function api<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...options,
    headers: options?.body ? { "content-type": "application/json", ...options.headers } : options?.headers,
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({ error: "Request failed" }))) as { error?: string };
    throw new Error(body.error ?? "Request failed");
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

function relativeDate(value: string | null): string {
  if (!value) return "Never";
  const time = new Date(value.replace(" ", "T") + (value.includes("Z") ? "" : "Z")).getTime();
  const days = Math.floor((Date.now() - time) / 86_400_000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days} days ago`;
  return new Intl.DateTimeFormat("en", { month: "short", day: "numeric", year: "numeric" }).format(new Date(time));
}

function renderShell(): void {
  app.innerHTML = `
    <header class="topbar">
      <a class="brand" href="#library" aria-label="Repbook home">
        <span class="brand-mark">R</span>
        <span>LeetCode Admin</span>
      </a>
      <nav aria-label="Main navigation">
        <a href="#library" data-view="library">Problems</a>
        <a href="#drills" data-view="drills">Drills</a>
        <a href="#tags" data-view="tags">Tags</a>
      </nav>
    </header>
    <main id="content"></main>
  `;
  app.addEventListener("click", handleClick);
  app.addEventListener("submit", handleSubmit);
  window.addEventListener("hashchange", navigate);
}

function setActiveNav(): void {
  document.querySelectorAll<HTMLElement>("[data-view]").forEach((link) => {
    link.classList.toggle("active", link.dataset.view === state.view);
  });
}

function content(): HTMLElement {
  const node = document.querySelector<HTMLElement>("#content");
  if (!node) throw new Error("Content mount is missing");
  return node;
}

function tagPills(tags: Tag[]): string {
  if (!tags.length) return "";
  return `<div class="pills">${tags.map((tag) => `<span class="pill">${escapeHtml(tag.name)}</span>`).join("")}</div>`;
}

function problemCard(problem: Problem, drill = false): string {
  const solution = problem.solutions[0];
  return `<article class="problem-card">
    <div class="problem-main">
      <div class="problem-heading">
        <span class="platform">${problem.platform}</span>
        <span class="difficulty ${problem.difficulty.toLowerCase()}">${problem.difficulty}</span>
        <span class="status">${escapeHtml(problem.status)}</span>
        ${problem.isFavorite ? '<span class="favorite" title="Favorite">★</span>' : ""}
      </div>
      <h3><a href="${escapeHtml(problem.url)}" target="_blank" rel="noreferrer">${problem.leetcodeId ? `${problem.leetcodeId}. ` : ""}${escapeHtml(problem.title)}</a></h3>
      <div class="problem-detail"><strong>Summary:</strong><p>${problem.summary ? escapeHtml(problem.summary) : "—"}</p></div>
      <div class="problem-detail notes"><strong>Notes:</strong><p>${problem.notes ? escapeHtml(problem.notes) : "—"}</p></div>
      ${tagPills(problem.tags)}
      ${solution?.codeUrl ? `<a class="solution-link" href="${escapeHtml(solution.codeUrl)}" target="_blank" rel="noreferrer">${escapeHtml(solution.language)} solution ↗</a>` : ""}
    </div>
    <aside class="problem-actions">
      ${drill ? `<div class="drill-meta"><span>Last drilled</span><strong>${relativeDate(problem.lastDrilledAt)}</strong><small>${problem.timesDrilled} completed</small></div>
        <button class="button primary" data-action="mark-drilled" data-id="${problem.id}">Mark drilled</button>` : ""}
      <button class="button ghost compact" data-action="edit-problem" data-id="${problem.id}">Edit</button>
      <button class="icon-button danger" data-action="delete-problem" data-id="${problem.id}" aria-label="Delete ${escapeHtml(problem.title)}">Delete</button>
    </aside>
  </article>`;
}

function renderLibrary(): void {
  content().innerHTML = `
    <section class="page-head">
      <div><h1>Problems</h1></div>
      <div class="add-actions">
        <button class="button primary" data-action="new-problem" data-platform="LeetCode">+ Add LeetCode</button>
        <button class="button secondary" data-action="new-problem" data-platform="CSES">+ Add CSES</button>
      </div>
    </section>
    <section class="toolbar" aria-label="Problem filters">
      <label class="search"><span>⌕</span><input id="search" placeholder="Search title or notes" value="${escapeHtml(state.filters.search)}" /></label>
      <select id="difficulty-filter" aria-label="Difficulty"><option value="">All difficulties</option>${["Easy", "Medium", "Hard"].map((value) => `<option ${state.filters.difficulty === value ? "selected" : ""}>${value}</option>`).join("")}</select>
      <select id="status-filter" aria-label="Status"><option value="">All statuses</option>${["To do", "Attempted", "Solved"].map((value) => `<option ${state.filters.status === value ? "selected" : ""}>${value}</option>`).join("")}</select>
      <select id="tag-filter" aria-label="Tag"><option value="">All tags</option>${state.tags.map((tag) => `<option value="${tag.id}" ${state.filters.tag === String(tag.id) ? "selected" : ""}>${escapeHtml(tag.name)}</option>`).join("")}</select>
    </section>
    <section class="list">
      ${state.problems.length ? state.problems.map((problem) => problemCard(problem)).join("") : emptyState("No problems match", "Add your first problem or clear the filters.", "new-problem", "Add problem")}
    </section>`;
  document.querySelector("#search")?.addEventListener("input", debounce(applyFilters, 250));
  for (const id of ["difficulty-filter", "status-filter", "tag-filter"]) document.querySelector(`#${id}`)?.addEventListener("change", applyFilters);
}

function renderDrills(): void {
  const eligible = state.problems.filter((problem) => problem.drillEnabled && problem.status === "Solved");
  content().innerHTML = `
    <section class="page-head">
      <div><h1>Implementation drills</h1></div>
      ${eligible.length > 1 ? '<button class="button secondary" data-action="random-drill">Pick random</button>' : ""}
    </section>
    <div class="drill-callout"><span class="pulse"></span><div><strong>${eligible.length} problem${eligible.length === 1 ? "" : "s"} in rotation</strong><p>Never drilled first, then oldest drilled.</p></div></div>
    <section class="list">
      ${eligible.length ? eligible.map((problem) => problemCard(problem, true)).join("") : emptyState("Your drill queue is empty", "Mark a solved problem as “Include in drills” to add it here.", "new-problem", "Add problem")}
    </section>`;
}

function renderTags(): void {
  content().innerHTML = `
    <section class="page-head">
      <div><h1>Tags</h1></div>
      <button class="button primary" data-action="new-tag">+ Create tag</button>
    </section>
    <section class="tag-grid">
      ${state.tags.length ? state.tags.map((tag) => `<article class="tag-card"><div><h3>${escapeHtml(tag.name)}</h3><p>${escapeHtml(tag.description || "No description")}</p></div><footer><span>${tag.problemCount ?? 0} problem${tag.problemCount === 1 ? "" : "s"}</span><div><button class="button ghost compact" data-action="edit-tag" data-id="${tag.id}">Edit</button><button class="icon-button danger" data-action="delete-tag" data-id="${tag.id}">Delete</button></div></footer></article>`).join("") : emptyState("No tags yet", "Create a few topics such as Graphs or Dynamic Programming.", "new-tag", "Create tag")}
    </section>`;
}

function emptyState(title: string, body: string, action: string, label: string): string {
  return `<div class="empty"><div class="empty-icon">◇</div><h2>${title}</h2><p>${body}</p><button class="button primary" data-action="${action}">${label}</button></div>`;
}

async function loadAll(): Promise<void> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(state.filters)) if (value) params.set(key, value);
  if (state.view === "drills") params.set("drills", "true");
  const [{ tags }, { problems }] = await Promise.all([
    api<{ tags: Tag[] }>("/api/tags"),
    api<{ problems: Problem[] }>(`/api/problems?${params}`),
  ]);
  state.tags = tags;
  state.problems = problems;
}

async function navigate(): Promise<void> {
  const requested = window.location.hash.slice(1);
  state.view = requested === "drills" || requested === "tags" ? requested : "library";
  setActiveNav();
  content().innerHTML = '<div class="loading">Loading…</div>';
  try {
    await loadAll();
    if (state.view === "library") renderLibrary();
    else if (state.view === "drills") renderDrills();
    else renderTags();
  } catch (error) {
    content().innerHTML = `<div class="empty"><h2>Could not load the app</h2><p>${escapeHtml(error instanceof Error ? error.message : "Unexpected error")}</p><button class="button primary" data-action="retry">Retry</button></div>`;
  }
}

async function applyFilters(): Promise<void> {
  state.filters = {
    search: (document.querySelector<HTMLInputElement>("#search")?.value ?? "").trim(),
    difficulty: document.querySelector<HTMLSelectElement>("#difficulty-filter")?.value ?? "",
    status: document.querySelector<HTMLSelectElement>("#status-filter")?.value ?? "",
    tag: document.querySelector<HTMLSelectElement>("#tag-filter")?.value ?? "",
  };
  await loadAll();
  renderLibrary();
}

function debounce<T extends (...args: never[]) => void>(fn: T, delay: number): (...args: Parameters<T>) => void {
  let timer = 0;
  return (...args) => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => fn(...args), delay);
  };
}

function problemDialog(problem?: Problem, initialPlatform: "LeetCode" | "CSES" = "LeetCode"): HTMLDialogElement {
  const platform = problem?.platform ?? initialPlatform;
  const dialog = document.createElement("dialog");
  dialog.className = "modal";
  dialog.innerHTML = `<form method="dialog" id="problem-form">
    <div class="modal-head"><h2>${problem ? "Edit problem" : `Add ${platform} problem`}</h2><button type="button" class="close" data-action="close-dialog" aria-label="Close">×</button></div>
    <div class="form-grid">
      <label>Platform<select name="platform"><option ${platform === "LeetCode" ? "selected" : ""}>LeetCode</option><option ${platform === "CSES" ? "selected" : ""}>CSES</option></select></label>
      <label>Problem number<input name="leetcodeId" type="number" min="1" value="${problem?.leetcodeId ?? ""}" placeholder="${platform === "CSES" ? "1068" : "1"}" /></label>
      <label class="span-2">Title<input name="title" required maxlength="200" value="${escapeHtml(problem?.title)}" placeholder="e.g. Two Sum" /></label>
      <label class="span-2">Problem URL<input name="url" type="url" required maxlength="500" value="${escapeHtml(problem?.url)}" placeholder="${platform === "CSES" ? "https://cses.fi/problemset/task/..." : "https://leetcode.com/problems/..."}" /></label>
      <label>Difficulty<select name="difficulty">${["Easy", "Medium", "Hard"].map((value) => `<option ${problem?.difficulty === value ? "selected" : ""}>${value}</option>`).join("")}</select></label>
      <label>Status<select name="status">${["To do", "Attempted", "Solved"].map((value) => `<option ${problem?.status === value || (!problem && value === "Solved") ? "selected" : ""}>${value}</option>`).join("")}</select></label>
      <label class="check-field"><input name="isFavorite" type="checkbox" ${problem?.isFavorite ? "checked" : ""} /> Favorite</label>
      <label class="span-2">Summary<textarea name="summary" maxlength="2000" rows="2" placeholder="Short problem summary">${escapeHtml(problem?.summary)}</textarea></label>
      <label class="span-2">Notes<textarea name="notes" maxlength="20000" rows="5" placeholder="Approach, mistakes, complexity…">${escapeHtml(problem?.notes)}</textarea></label>
      <fieldset class="span-2"><legend>Tags</legend><div class="tag-options">${state.tags.map((tag) => `<label><input type="checkbox" name="tagIds" value="${tag.id}" ${problem?.tags.some((entry) => entry.id === tag.id) ? "checked" : ""} /> ${escapeHtml(tag.name)}</label>`).join("") || '<span class="muted">Create tags from the Tags page.</span>'}</div></fieldset>
      <fieldset class="span-2"><legend>Drills</legend><label class="check-field"><input name="drillEnabled" type="checkbox" ${problem?.drillEnabled !== false ? "checked" : ""} /> Include this problem in implementation drills</label></fieldset>
      <fieldset class="span-2 solutions"><div class="legend-row"><legend>Solutions (optional)</legend><button type="button" class="button ghost compact" data-action="add-solution">+ Add solution</button></div><div id="solution-list">${(problem?.solutions ?? []).map(solutionFields).join("")}</div></fieldset>
    </div>
    <div class="modal-actions"><button type="button" class="button ghost" data-action="close-dialog">Cancel</button><button class="button primary" value="default" data-action="save-problem" data-id="${problem?.id ?? ""}">${problem ? "Save changes" : "Add problem"}</button></div>
  </form>`;
  document.body.append(dialog);
  dialog.addEventListener("click", handleClick);
  dialog.addEventListener("submit", handleSubmit);
  dialog.addEventListener("close", () => dialog.remove());
  dialog.showModal();
  return dialog;
}

function solutionFields(solution?: Solution): string {
  return `<div class="solution-row"><label>Language<input name="solutionLanguage" maxlength="50" value="${escapeHtml(solution?.language ?? "C++")}" /></label><label>Code URL<input name="solutionUrl" type="url" maxlength="500" value="${escapeHtml(solution?.codeUrl)}" placeholder="GitHub or LeetCode submission" /></label><label class="grow">Note<input name="solutionNotes" maxlength="5000" value="${escapeHtml(solution?.notes)}" placeholder="Optional" /></label><button type="button" class="close small" data-action="remove-solution" aria-label="Remove solution">×</button></div>`;
}

function tagDialog(tag?: Tag): HTMLDialogElement {
  const dialog = document.createElement("dialog");
  dialog.className = "modal small-modal";
  dialog.innerHTML = `<form method="dialog" id="tag-form"><div class="modal-head"><h2>${tag ? "Edit tag" : "Create tag"}</h2><button type="button" class="close" data-action="close-dialog" aria-label="Close">×</button></div><div class="form-stack"><label>Name<input name="name" required maxlength="80" value="${escapeHtml(tag?.name)}" placeholder="e.g. Sliding Window" /></label><label>Description<textarea name="description" maxlength="500" rows="3" placeholder="When this pattern applies">${escapeHtml(tag?.description)}</textarea></label></div><div class="modal-actions"><button type="button" class="button ghost" data-action="close-dialog">Cancel</button><button class="button primary" value="default" data-action="save-tag" data-id="${tag?.id ?? ""}">${tag ? "Save changes" : "Create tag"}</button></div></form>`;
  document.body.append(dialog);
  dialog.addEventListener("click", handleClick);
  dialog.addEventListener("submit", handleSubmit);
  dialog.addEventListener("close", () => dialog.remove());
  dialog.showModal();
  return dialog;
}

async function saveProblem(button: HTMLElement): Promise<void> {
  const form = button.closest<HTMLFormElement>("form");
  if (!form || !form.reportValidity()) return;
  const data = new FormData(form);
  const solutionRows = [...form.querySelectorAll<HTMLElement>(".solution-row")];
  const input: ProblemInput = {
    platform: String(data.get("platform")) as ProblemInput["platform"],
    title: String(data.get("title") ?? ""),
    url: String(data.get("url") ?? ""),
    leetcodeId: data.get("leetcodeId") ? Number(data.get("leetcodeId")) : null,
    difficulty: String(data.get("difficulty")) as ProblemInput["difficulty"],
    status: String(data.get("status")) as ProblemInput["status"],
    summary: String(data.get("summary") ?? ""),
    notes: String(data.get("notes") ?? ""),
    isFavorite: data.has("isFavorite"),
    drillEnabled: data.has("drillEnabled"),
    tagIds: data.getAll("tagIds").map(Number),
    solutions: solutionRows.map((row) => ({
      language: row.querySelector<HTMLInputElement>('[name="solutionLanguage"]')?.value ?? "",
      codeUrl: row.querySelector<HTMLInputElement>('[name="solutionUrl"]')?.value ?? "",
      notes: row.querySelector<HTMLInputElement>('[name="solutionNotes"]')?.value ?? "",
    })),
  };
  button.setAttribute("disabled", "");
  try {
    const id = button.dataset.id;
    await api(id ? `/api/problems/${id}` : "/api/problems", { method: id ? "PUT" : "POST", body: JSON.stringify(input) });
    form.closest<HTMLDialogElement>("dialog")?.close();
    showToast(id ? "Problem updated" : "Problem added");
    await navigate();
  } catch (error) {
    showToast(error instanceof Error ? error.message : "Could not save problem", "error");
    button.removeAttribute("disabled");
  }
}

async function saveTag(button: HTMLElement): Promise<void> {
  const form = button.closest<HTMLFormElement>("form");
  if (!form || !form.reportValidity()) return;
  const data = new FormData(form);
  button.setAttribute("disabled", "");
  try {
    const id = button.dataset.id;
    await api(id ? `/api/tags/${id}` : "/api/tags", { method: id ? "PUT" : "POST", body: JSON.stringify({ name: data.get("name"), description: data.get("description") }) });
    form.closest<HTMLDialogElement>("dialog")?.close();
    showToast(id ? "Tag updated" : "Tag created");
    await navigate();
  } catch (error) {
    showToast(error instanceof Error ? error.message : "Could not save tag", "error");
    button.removeAttribute("disabled");
  }
}

async function handleClick(event: MouseEvent): Promise<void> {
  const target = (event.target as HTMLElement).closest<HTMLElement>("[data-action]");
  if (!target) return;
  const action = target.dataset.action;
  if (action === "close-dialog") target.closest<HTMLDialogElement>("dialog")?.close();
  else if (action === "new-problem") problemDialog(undefined, target.dataset.platform === "CSES" ? "CSES" : "LeetCode");
  else if (action === "edit-problem") {
    const problem = state.problems.find((entry) => entry.id === Number(target.dataset.id));
    if (problem) problemDialog(problem);
  } else if (action === "save-problem") {
    event.preventDefault();
    await saveProblem(target);
  } else if (action === "delete-problem") {
    const problem = state.problems.find((entry) => entry.id === Number(target.dataset.id));
    if (problem && window.confirm(`Delete “${problem.title}”? This cannot be undone.`)) {
      await api(`/api/problems/${problem.id}`, { method: "DELETE" });
      showToast("Problem deleted");
      await navigate();
    }
  } else if (action === "mark-drilled") {
    await api(`/api/problems/${target.dataset.id}/drill`, { method: "POST" });
    showToast("Drill completed");
    await navigate();
  } else if (action === "random-drill") {
    const cards = [...document.querySelectorAll<HTMLElement>(".problem-card")];
    cards[Math.floor(Math.random() * cards.length)]?.scrollIntoView({ behavior: "smooth", block: "center" });
  } else if (action === "new-tag") tagDialog();
  else if (action === "edit-tag") {
    const tag = state.tags.find((entry) => entry.id === Number(target.dataset.id));
    if (tag) tagDialog(tag);
  } else if (action === "save-tag") {
    event.preventDefault();
    await saveTag(target);
  } else if (action === "delete-tag") {
    const tag = state.tags.find((entry) => entry.id === Number(target.dataset.id));
    if (tag && window.confirm(`Delete the “${tag.name}” tag? Problems will be kept.`)) {
      await api(`/api/tags/${tag.id}`, { method: "DELETE" });
      showToast("Tag deleted");
      await navigate();
    }
  } else if (action === "add-solution") document.querySelector("#solution-list")?.insertAdjacentHTML("beforeend", solutionFields());
  else if (action === "remove-solution") target.closest(".solution-row")?.remove();
  else if (action === "retry") await navigate();
}

function handleSubmit(event: SubmitEvent): void {
  if ((event.submitter as HTMLElement | null)?.getAttribute("value") === "cancel") return;
  event.preventDefault();
}

renderShell();
void navigate();
